import React, { useState } from "react";
import {
  Paper,
  Typography,
  Grid,
  Box,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import {
  AutoAwesomeMosaic,
  ContactPage,
  MenuBook,
  NewReleases,
  Person,
  Notifications,
  Message,
  Settings,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useSelector } from "react-redux";
import { alpha } from "@mui/material/styles";
import { RootState } from "../../store/Index";

const MotionCard = motion(Box);

interface ISettingsCardProps {
  title: string;
  description: string;
  icon?: React.ReactNode;
  disabled?: boolean;
  navigateTo?: string;
  isSwitchable?: boolean;
  initialSwitchState?: boolean;
  onSwitchChange?: (checked: boolean) => void;
  category: string;
  thumbnail_type?: string;
}

const SettingsCard: React.FC<ISettingsCardProps> = ({
  title,
  description,
  icon,
  disabled = false,
  navigateTo,
  isSwitchable = false,
  initialSwitchState = false,
  onSwitchChange,
  thumbnail_type,
}) => {
  const navigate = useNavigate();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [isOn, setIsOn] = useState(initialSwitchState);

  const primaryTextColor = reduxTheme.primary_colors?.[100] || "#9c6644";
  const secondaryTextColor = reduxTheme.primary_colors?.[100] || "#D6DDEB";
  const backgroundColor = reduxTheme.primary_colors?.[900] || "#A9B2C3";

  const handleCardClick = () => {
    if (!disabled && navigateTo) {
      navigate(
        `${navigateTo}${
          thumbnail_type ? `?thumbnail_type=${thumbnail_type}` : ""
        }`,
      );
    }
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsOn(event.target.checked);
    if (onSwitchChange) {
      onSwitchChange(event.target.checked);
    }
  };

  return (
    <Paper
      sx={{
        boxShadow: 0,
        padding: "16px",
        textAlign: "left",
        opacity: disabled ? 0.6 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
        height: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundColor: backgroundColor,
      }}
      onClick={!isSwitchable ? handleCardClick : undefined}
    >
      <Box>
        {icon && <Box sx={{ mb: "8px" }}>{icon}</Box>}
        <Typography variant="h6" gutterBottom sx={{ color: primaryTextColor }}>
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: secondaryTextColor }}
          gutterBottom
        >
          {description}
        </Typography>
      </Box>

      {isSwitchable ? (
        <FormControlLabel
          control={
            <Switch
              checked={isOn}
              onChange={handleSwitchChange}
              sx={{
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: primaryTextColor,
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: primaryTextColor,
                },
              }}
            />
          }
          label=""
          labelPlacement="end"
          sx={{ ml: "auto", mr: 0, alignSelf: "flex-end" }}
        />
      ) : (
        <Box sx={{ height: "48px" }} />
      )}

      {disabled && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: alpha(backgroundColor, 0.7),
            zIndex: 1,
          }}
        >
          <LockIcon sx={{ color: secondaryTextColor }} fontSize="large" />
        </Box>
      )}
    </Paper>
  );
};

const Configurations: React.FC = () => {
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const [activeCategory, setActiveCategory] = useState("All");

  const primaryColor = reduxTheme.primary_colors?.[100] || "#724E41";
  const primaryTextColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const backgroundColor = reduxTheme.primary_colors?.[900] || "#A9B2C3";

  const handleTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setActiveCategory(newValue);
  };

  const allSettings = [
    {
      icon: <Person sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "Packagings & Pricing",
      description: "Manage funeral streaming packages (X, Pro, Lux)",
      navigateTo: "/configurations/package",
      category: "Bookings",
    },
    {
      icon: <NewReleases sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "Thumbnail Templates",
      description: "Add and manage your thumbnail designs",
      navigateTo: "/configurations/thumbnail-template-manager",
      category: "Streams",
      thumbnail_type: "youtube_thumbnail",
    },
    {
      icon: <NewReleases sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "A1 Templates",
      description: "Add and manage your AI designs",
      navigateTo: "/configurations/thumbnail-template-manager",
      category: "Streams",
      thumbnail_type: "aone",
    },
    {
      icon: <Message sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "Share Message Templates",
      description: "Create and manage social media share templates",
      navigateTo: "/configurations/share-messages",
      category: "Streams",
    },
    {
      icon: <NewReleases sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "Proposal Cover Templates",
      description: "Add and manage your cover page designs",
      navigateTo: "/configurations/proposal-cover-page",
      category: "Proposal",
    },
    {
      icon: <Message sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "Proposal Introduction Letter Templates",
      description: "Add and manage your introduction letters on proposals",
      navigateTo: "/configurations/proposal-introduction-letter",
      category: "Proposal",
    },
    {
      icon: <Message sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "Customer Testimonials",
      description: "Add and manage your customer testimonials",
      navigateTo: "/configurations/testimonials",
      category: "Proposal",
    },

    {
      icon: <LockIcon sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "Roles and Permissions",
      description: "Control access levels across all departments",
      navigateTo: "/configurations/manage-roles",
      category: "User Management",
    },
    {
      icon: <Settings sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "SEO Management",
      description: "Configure metadata and keywords for all your pages",
      navigateTo: "/configurations/seo-management",
      category: "Seo Management",
    },
    {
      icon: (
        <AutoAwesomeMosaic
          sx={{ color: primaryColor, width: 40, height: 40 }}
        />
      ),
      title: "Change Signin Background",
      description: "Change the background image for the sign-in page",
      navigateTo: "/changebackground",
      disabled: true,
      category: "All",
    },
    {
      icon: <MenuBook sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "Company Types",
      description: "Add, view, and remove company types for contacts",
      navigateTo: "/contacts",
      disabled: true,
      category: "Contacts",
    },
    {
      icon: <ContactPage sx={{ color: primaryColor, width: 40, height: 40 }} />,
      title: "Contact Management",
      description:
        "Manage company and individual contacts linked to operations",
      navigateTo: "/contacts",
      disabled: true,
      category: "Contacts",
    },
    {
      icon: (
        <Notifications sx={{ color: primaryColor, width: 40, height: 40 }} />
      ),
      title: "Notification Settings",
      description: "Configure email and in-app notifications",
      navigateTo: "/notifications",
      category: "Notifications",
      disabled: true,
    },
    {
      icon: (
        <AutoAwesomeMosaic
          sx={{ color: primaryColor, width: 40, height: 40 }}
        />
      ),
      title: "Venues Administration",
      description: "Manage venue dashboard widgets and display settings",
      navigateTo: "/configurations/venues-admin",
      category: "Components Management",
    },

    {
      icon: (
        <AutoAwesomeMosaic
          sx={{ color: primaryColor, width: 40, height: 40 }}
        />
      ),
      title: "Components manager",
      description: "Manage components and their leads",
      navigateTo: "/configurations/components-manager",
      category: "Venues",
    },
  ];

  const categories = [
    "All",
    "Bookings",
    "Streams",
    "Proposal",
    "Venues",
    "Contacts",
    "User Management",
    "Notifications",
    "Components Management",
    "Seo Management",
  ];

  const filteredSettings =
    activeCategory === "All"
      ? allSettings
      : allSettings.filter((setting) => setting.category === activeCategory);

  return (
    <Box>
      <Tabs
        value={activeCategory}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 3,
          "& .MuiTabs-indicator": {
            backgroundColor: primaryColor,
          },
          "& .MuiTab-root": {
            textTransform: "none",
            fontWeight: "medium",
            fontSize: "1rem",
            color: primaryTextColor,
            "&.Mui-selected": {
              color: primaryColor,
            },
          },
        }}
      >
        {categories.map((category) => (
          <Tab key={category} label={category} value={category} />
        ))}
      </Tabs>

      <Grid container spacing={2}>
        {filteredSettings.map((setting, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={setting.title}>
            <MotionCard
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.15 }}
              whileHover={{
                scale: 1.03,
                boxShadow: `0px 8px 20px ${alpha(backgroundColor, 0.1)}`,
                transition: { duration: 0.3 },
              }}
            >
              <SettingsCard {...setting} />
            </MotionCard>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default Configurations;
